// The library's API client. One place that knows the shelf's endpoints.
//
// Every tab payload is ANONYMOUS: the tab endpoints contain no personalisation, which is
// what lets them be cached. Everything per-reader comes from `/library/me/shelf`, fetched
// separately and only when signed in.

import { api } from "./api";
import type {
  BrowsePayload,
  BrowseSort,
  IllustratedFilter,
  LengthBand,
  MyShelfPayload,
  ReadingNowPayload,
  StorePayload,
} from "./library-types";

/** `auth: false` so a logged-out visitor gets the shelf rather than a 401. */
const PUBLIC = { auth: false } as const;

export function fetchReadingNow(): Promise<ReadingNowPayload> {
  return api.get<ReadingNowPayload>("/api/library/tabs/reading-now", PUBLIC);
}

export function fetchStore(chartSort: "newest" | "longest"): Promise<StorePayload> {
  return api.get<StorePayload>(`/api/library/tabs/store?chart_sort=${chartSort}`, PUBLIC);
}

export interface BrowseQuery {
  kind: string | null;
  lengths: LengthBand[];
  illustrated: IllustratedFilter[];
  sort: BrowseSort;
  /** Free text, matched server-side against title and byline. */
  q?: string | null;
  /** 1-based. What a numbered pager needs; `cursor` cannot express a jump. */
  page?: number;
  cursor?: string | null;
  limit?: number;
}

export function browseParams(query: BrowseQuery): string {
  const params = new URLSearchParams();
  if (query.kind) params.append("kind", query.kind);
  // Repeated params, not comma-joined: the server ORs within a facet, and FastAPI reads a
  // repeated key as a list.
  for (const band of query.lengths) params.append("length", band);
  for (const value of query.illustrated) params.append("plates", value);
  // Trimmed here as well as server-side: an all-space `q` on the wire is noise that would
  // also defeat the response cache for no benefit.
  const q = query.q?.trim();
  if (q) params.set("q", q);
  if (query.sort !== "newest") params.set("sort", query.sort);
  // Page 1 is the default, so it stays off the wire and off the URL.
  if (query.page && query.page > 1) params.set("page", String(query.page));
  if (query.cursor) params.set("cursor", query.cursor);
  if (query.limit) params.set("limit", String(query.limit));
  return params.toString();
}

export function fetchBrowse(query: BrowseQuery): Promise<BrowsePayload> {
  const qs = browseParams(query);
  return api.get<BrowsePayload>(`/api/library/browse${qs ? `?${qs}` : ""}`, PUBLIC);
}

/** Signed-in only — the caller must not call this for an anonymous visitor. */
export function fetchMyShelf(): Promise<MyShelfPayload> {
  return api.get<MyShelfPayload>("/api/library/me/shelf");
}
